import type { AmsAuditApproval } from '../../../services/amsAuditApprovalService';
import type { AmsProjectAudit } from '../../../services/amsProjectAuditService';
import { resolveAmsProjectId } from './resolveAmsProjectId';
import { getAuditBucket, auditNeedsReview } from './auditApprovalStatus';

export type ProjectTone = 'pend' | 'rej' | 'appr' | 'default';

export interface AuditApprovalItem {
  approval: AmsAuditApproval;
  audit: AmsProjectAudit | null;
}

export interface ProjectApprovalGroup {
  projectId: string;
  project: any | null;
  projectName: string;
  audits: AuditApprovalItem[];
  needsReview: boolean;
  counts: { pending: number; rejected: number; approved: number };
  tone: ProjectTone;
}

export interface GroupedApprovals {
  needsReview: ProjectApprovalGroup[];
  noAction: ProjectApprovalGroup[];
}

export interface ProjectApprovalStats {
  awaitingReview: number;
  withRejections: number;
  approvedToStart: number;
  totalProjects: number;
  auditsAwaiting: number;
  auditsRejected: number;
  auditsApproved: number;
  totalAudits: number;
}

const UNGROUPED = '__ungrouped__';

const STATUS_ORDER: Record<AmsAuditApproval['overallStatus'], number> = {
  pending_review: 0,
  partially_approved: 1,
  rejected: 2,
  approved_to_start: 3,
  draft: 4,
};

/**
 * Group audit approvals by project, dropping draft audits (and any project left
 * with no non-draft audits). Returns two priority bands: projects with a pending
 * audit ("needs your review") and everything else ("no action needed").
 */
export const groupApprovalsByProject = (
  approvals: AmsAuditApproval[],
  audits: Map<string, AmsProjectAudit>,
  projects: Map<string, any>,
): GroupedApprovals => {
  const groups = new Map<string, ProjectApprovalGroup>();

  for (const approval of approvals) {
    if (getAuditBucket(approval) === null) continue; // drop drafts

    const audit = audits.get(approval.amsProjectAuditId) || null;
    const projectId = (audit && resolveAmsProjectId(audit.amsProjectId)) || UNGROUPED;
    const project = projectId === UNGROUPED ? null : projects.get(projectId) || null;

    let group = groups.get(projectId);
    if (!group) {
      group = {
        projectId,
        project,
        projectName: project?.projectName || project?.name || 'Unassigned project',
        audits: [],
        needsReview: false,
        counts: { pending: 0, rejected: 0, approved: 0 },
        tone: 'default',
      };
      groups.set(projectId, group);
    }

    group.audits.push({ approval, audit });
    const bucket = getAuditBucket(approval);
    if (bucket) group.counts[bucket] += 1;
    if (auditNeedsReview(approval)) group.needsReview = true;
  }

  const finalize = (group: ProjectApprovalGroup): ProjectApprovalGroup => {
    group.audits.sort(
      (a, b) => STATUS_ORDER[a.approval.overallStatus] - STATUS_ORDER[b.approval.overallStatus],
    );
    group.tone =
      group.counts.pending > 0
        ? 'pend'
        : group.counts.rejected > 0
          ? 'rej'
          : group.counts.approved > 0
            ? 'appr'
            : 'default';
    return group;
  };

  const all = [...groups.values()].map(finalize);
  return {
    needsReview: all.filter((g) => g.needsReview),
    // Rejected projects sort ahead of fully-approved ones within the "no action" band.
    noAction: all
      .filter((g) => !g.needsReview)
      .sort((a, b) => (a.counts.rejected > 0 ? 0 : 1) - (b.counts.rejected > 0 ? 0 : 1)),
  };
};

export const deriveProjectApprovalStats = (grouped: GroupedApprovals): ProjectApprovalStats => {
  const all = [...grouped.needsReview, ...grouped.noAction];
  const stats: ProjectApprovalStats = {
    awaitingReview: 0,
    withRejections: 0,
    approvedToStart: 0,
    totalProjects: all.length,
    auditsAwaiting: 0,
    auditsRejected: 0,
    auditsApproved: 0,
    totalAudits: 0,
  };
  for (const g of all) {
    // Each project is counted in exactly ONE status tile, by its dominant tone
    // (same priority as the bands: pending > rejected > approved). So
    // awaitingReview + withRejections + approvedToStart === totalProjects.
    if (g.tone === 'pend') stats.awaitingReview += 1;
    else if (g.tone === 'rej') stats.withRejections += 1;
    else if (g.tone === 'appr') stats.approvedToStart += 1;
    // Audit-level tallies stay per-audit (an audit is always in one bucket).
    stats.auditsAwaiting += g.counts.pending;
    stats.auditsRejected += g.counts.rejected;
    stats.auditsApproved += g.counts.approved;
    stats.totalAudits += g.audits.length;
  }
  return stats;
};
